import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import NotesModel from "./NotesModel";
import VersesModel from "./VersesModel";
import VerseDetails from "./VerseDetails";
import Toastiy from "../../components/common/Toastiy";
import AudioVideoCompModel from "./AudioVideoCompModel";
import { copyToClipboard } from "../../utils/Utils";
import { versesColor } from "../../constants/common";

import {
  deleteVerseColorAction,
  getBibleSummaryAction,
  getChapterDetailsAction,
  getVerseColorAction,
  getVerseDetailsAction,
  saveVerseColorAction,
  updateVerseColorAction,
} from "../../store/Bible/BibleAction";
import { userFavAction, userUnFavAction } from "../../store/Home/HomeAction";

import { useAuth0 } from "@auth0/auth0-react";
import { MdOutlineBookmark } from "react-icons/md";
import HoverJournals from "../../assets/icons/NavIcons/hoverJournals.svg";
import BibleVersionControl from "../../components/bible/BibleVersionControl";
import ParallelVerses from "../../components/bible/ParallelVerses";
import * as bibleApi from "../../store/Bible/BibleAPI";
import * as homeApi from "../../store/Home/HomeAPI";

const ChapterDetails = ({
  chapterNo,
  className = "",
  versionDetails = {},
  bibleParallelMode = false,
  updateActiveVerseDetails = () => {},
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const { bookId } = useParams();
  const { isAuthenticated } = useAuth0();

  const { bibleLanguage, bibleParallelMode: globalBibleParallelMode } =
    useSelector((state) => state.bible);
  const { isMobile } = useSelector((state) => state.common);

  const [active, setActive] = useState(-1);
  const [activeVerseDetails, setActiveVerseDetails] = useState({});
  const [selectedVerses, setSelectedVerses] = useState({});
  const [selectedVerseItems, setSelectedVerseItems] = useState({});
  const [isMultiColorPaletteOpen, setIsMultiColorPaletteOpen] = useState(false);
  const [isMultiActionBusy, setIsMultiActionBusy] = useState(false);
  const [isNotesActive, setIsNotesActive] = useState(false);
  const [isNewNotesActive, setIsNewNotesActive] = useState(false);
  const [isNotesPopupActive, setIsNotesPopupActive] = useState(false);
  const [existingJournalId, setExistingJournalId] = useState(null);
  const [isTextAvailable, setIsTextAvailable] = useState(false);
  const [bookChapterVerseDetails, setBookChapterVerseDetails] = useState([]);
  const [bookChapterVerseDetailsLoading, setBookChapterVerseDetailsLoading] =
    useState(false);
  const [isAudioAvailable, setIsAudioAvailable] = useState(false);
  const [bookChapterAudioDetails, setBookChapterAudioDetails] = useState([]);
  const [bookChapterAudioDetailsLoading, setBookChapterAudioDetailsLoading] =
    useState(false);
  const [isVideoAvailable, setIsVideoAvailable] = useState(false);
  const [bookChapterVideoDetails, setBookChapterVideoDetails] = useState([]);
  const [bookChapterVideoDetailsLoading, setBookChapterVideoDetailsLoading] =
    useState(false);
  const [bibleSummary, setBibleSummary] = useState({});

  const selectedVerseEntries = useMemo(
    () =>
      Object.values(selectedVerseItems).sort(
        (a, b) => Number(a?.index ?? 0) - Number(b?.index ?? 0)
      ),
    [selectedVerseItems]
  );
  const selectedVerseCount = selectedVerseEntries.length;

  const getVerseSelectionKey = (item) =>
    `${versionDetails?.abbr || ""}${bookId}${chapterNo}${item?.verse_start}`;

  const clearMultiSelection = () => {
    setSelectedVerseItems({});
    setIsMultiColorPaletteOpen(false);
    setActive(-1);
  };

  const toggleVerseMultiSelection = (index, item) => {
    const key = getVerseSelectionKey(item);
    setActive(-1);
    setSelectedVerseItems((prev) => {
      const newData = { ...prev };
      if (newData[key]) {
        delete newData[key];
      } else {
        newData[key] = { key, index, item };
      }
      return newData;
    });
  };

  const resolveVerseDetails = async (entry) => {
    const verseItem = entry?.item;
    const response = await bibleApi.getVerseDetails({
      languageid: bibleLanguage,
      versionid: versionDetails?.abbr,
      bookid: bookId,
      bookname: bookName,
      chapterid: chapterNo,
      verse_start: verseItem?.verse_start,
      verse_end: verseItem?.verse_end,
      verseText: verseItem?.verse_text,
    });
    const verseDetails = response?.data?.[0];
    const verseObjId = verseDetails?.verses?.[0]?.verseObjId;
    if (!verseDetails?._id || !verseObjId) {
      throw new Error("Verse details not available");
    }
    return { ...entry, chapterObjId: verseDetails._id, verseObjId };
  };

  const resolveSelectedVerseEntries = async () => {
    const resolvedEntries = [];
    for (const entry of selectedVerseEntries) {
      const resolvedEntry = await resolveVerseDetails(entry);
      resolvedEntries.push(resolvedEntry);
    }
    return resolvedEntries;
  };

  const refreshVerseUserState = () => {
    fetchVerseColors();
    fetchBibleSummary(versionDetails?.abbr, bibleLanguage);
  };

  const handleMultiColorChange = async (color) => {
    if (!isAuthenticated || selectedVerseCount < 1 || isMultiActionBusy) return;
    setIsMultiActionBusy(true);
    try {
      const resolvedEntries = await resolveSelectedVerseEntries();
      setSelectedVerses((prev) => {
        const newData = { ...prev };
        resolvedEntries.forEach(({ item, chapterObjId, verseObjId }) => {
          newData[`${bookId}${item?.verse_start}`] = {
            color,
            verseDetails: [
              {
                chapterObjId,
                verseObjId,
                verse_start: `${item?.verse_start}`,
              },
            ],
          };
        });
        return newData;
      });

      for (const { item, chapterObjId, verseObjId } of resolvedEntries) {
        const existingVerseObjId =
          selectedVerses?.[`${bookId}${item?.verse_start}`]?.verseDetails?.[0]
            ?.verseObjId;
        if (existingVerseObjId) {
          await bibleApi.updateVerseColor({
            color: color.replaceAll("#", ""),
            versuObjId: existingVerseObjId,
          });
        } else {
          await bibleApi.saveVerseColor({
            color: color.replaceAll("#", ""),
            chapterObjId,
            versuObjId: verseObjId,
          });
        }
      }
      refreshVerseUserState();
      clearMultiSelection();
      Toastiy("Verses highlighted successfully", 1);
    } catch (error) {
      Toastiy("Unable to highlight selected verses", 2);
    } finally {
      setIsMultiActionBusy(false);
    }
  };

  const handleMultiRemoveColor = async () => {
    if (!isAuthenticated || selectedVerseCount < 1 || isMultiActionBusy) return;
    setIsMultiActionBusy(true);
    try {
      for (const { item } of selectedVerseEntries) {
        const existingVerseObjId =
          selectedVerses?.[`${bookId}${item?.verse_start}`]?.verseDetails?.[0]
            ?.verseObjId;
        if (existingVerseObjId) {
          await bibleApi.deleteVerseColor({ versuObjId: existingVerseObjId });
        }
      }
      setSelectedVerses((prev) => {
        const newData = { ...prev };
        selectedVerseEntries.forEach(({ item }) => {
          delete newData[`${bookId}${item?.verse_start}`];
        });
        return newData;
      });
      refreshVerseUserState();
      clearMultiSelection();
      Toastiy("Verse highlights cleared", 1);
    } catch (error) {
      Toastiy("Unable to clear selected highlights", 2);
    } finally {
      setIsMultiActionBusy(false);
    }
  };

  const handleMultiSaveVerse = async () => {
    if (!isAuthenticated || selectedVerseCount < 1 || isMultiActionBusy) return;
    setIsMultiActionBusy(true);
    try {
      const resolvedEntries = await resolveSelectedVerseEntries();
      for (const { chapterObjId, verseObjId } of resolvedEntries) {
        await homeApi.userFav({
          type: "4",
          chapterObjId,
          versuObjId: verseObjId,
        });
      }
      refreshVerseUserState();
      clearMultiSelection();
      Toastiy("Verses saved successfully", 1);
    } catch (error) {
      Toastiy("Unable to save selected verses", 2);
    } finally {
      setIsMultiActionBusy(false);
    }
  };

  const handleMultiCopy = () => {
    if (selectedVerseCount < 1) return;
    const reference = `${bookName} ${chapterNo}:${selectedVerseEntries
      .map(({ item }) => item?.verse_start)
      .join(", ")}`;
    const verseText = selectedVerseEntries
      .map(({ item }) => `[${item?.verse_start}] ${item?.verse_text}`)
      .join("\n");
    copyToClipboard(`${reference}\n${verseText}`);
    clearMultiSelection();
    Toastiy("Copied to clipboard!", 1);
  };


  const handleColorChange = (color, index, item) => {
    setSelectedVerses((prev) => {
      let newData = { ...prev };
      newData[`${bookId}${item?.verse_start}`] = { color: color };

      return newData;
    });
    setActive(-1);
    if (
      selectedVerses?.[`${bookId}${item?.verse_start}`]?.verseDetails?.[0]
        ?.verseObjId
    ) {
      dispatch(
        updateVerseColorAction({
          apiPayloadRequest: {
            color: color.replaceAll("#", ""),
            versuObjId:
              selectedVerses?.[`${bookId}${item?.verse_start}`]
                ?.verseDetails?.[0]?.verseObjId,
          },
          callback: (response) => {
            setActive(-1);
          },
        })
      );
    } else {
      dispatch(
        getVerseDetailsAction({
          apiPayloadRequest: {
            languageid: bibleLanguage,
            versionid: versionDetails?.abbr,
            bookid: bookId,
            bookname: bookName,
            chapterid: chapterNo,
            verse_start: bookChapterVerseDetails?.[index]?.verse_start,
            verse_end: bookChapterVerseDetails?.[index]?.verse_end,
            verseText: bookChapterVerseDetails?.[index]?.verse_text,
          },
          callback: (response) => {
            const verseDetails = response?.data?.[0];

            dispatch(
              saveVerseColorAction({
                apiPayloadRequest: {
                  color: color.replaceAll("#", ""),
                  chapterObjId: verseDetails?._id,
                  versuObjId: verseDetails?.verses?.[0]?.verseObjId,
                },
                callback: (response) => {
                  setSelectedVerses((prev) => {
                    let newData = { ...prev };
                    newData[`${bookId}${item?.verse_start}`] = {
                      color: color,
                      verseDetails: [
                        {
                          chapterObjId: verseDetails?._id,
                          verseObjId: verseDetails?.verses?.[0]?.verseObjId,
                          verse_start:
                            bookChapterVerseDetails?.[index]?.verse_start + "",
                        },
                      ],
                    };

                    return newData;
                  });
                  setActive(-1);
                },
              })
            );
          },
        })
      );
    }
  };

  const handleRemoveColor = (index, item) => {
    setSelectedVerses((prev) => {
      let newData = { ...prev };
      delete newData[`${bookId}${item?.verse_start}`];

      return newData;
    });
    setActive(-1);
    if (
      selectedVerses?.[`${bookId}${item?.verse_start}`]?.verseDetails?.[0]
        ?.verseObjId
    ) {
      dispatch(
        deleteVerseColorAction({
          apiPayloadRequest: {
            versuObjId:
              selectedVerses?.[`${bookId}${item?.verse_start}`]
                ?.verseDetails?.[0]?.verseObjId,
          },
          callback: (response) => {
            setActive(-1);
          },
        })
      );
    }
  };

  const handleSaveVerse = (index) => {
    dispatch(
      getVerseDetailsAction({
        apiPayloadRequest: {
          languageid: bibleLanguage,
          versionid: versionDetails?.abbr,
          bookid: bookId,
          bookname: bookName,
          chapterid: chapterNo,
          verse_start: bookChapterVerseDetails?.[index]?.verse_start,
          verse_end: bookChapterVerseDetails?.[index]?.verse_end,
          verseText: bookChapterVerseDetails?.[index]?.verse_text,
        },
        callback: (response) => {
          const verseDetails = response?.data?.[0];
          dispatch(
            userFavAction({
              apiPayloadRequest: {
                type: "4",
                chapterObjId: verseDetails?._id,
                versuObjId: verseDetails?.verses?.[0]?.verseObjId,
              },
              callback: (response) => {
                setActive(-1);
                Toastiy("Verse Saved Successfully", 1);
                fetchBibleSummary(versionDetails?.abbr, bibleLanguage);
              },
            })
          );
        },
      })
    );
  };

  const handleRemoveVerse = (chapterObjId, verseObjId) => {
    dispatch(
      userUnFavAction({
        apiPayloadRequest: {
          type: "4",
          chapterObjId: chapterObjId,
          versuObjId: verseObjId,
        },
        callback: (response) => {
          setActive(-1);
          Toastiy("Verse Removed", 1);
          fetchBibleSummary(versionDetails?.abbr, bibleLanguage);
        },
      })
    );
  };

  const fetchChapterVideoDetails = () => {
    setBookChapterVideoDetailsLoading(true);
    const bookDetails = versionDetails?.books?.find(
      (book) => book?.book_id === bookId
    );
    const filesets = [
      ...(versionDetails?.filesets?.["dbp-prod"]?.filter(
        (d) =>
          (d?.size?.includes(bookDetails?.testament) || d?.size === "C") &&
          d?.type === "video_stream"
      ) ?? []),
      ...(versionDetails?.filesets?.["dbp-vid"]?.filter(
        (d) =>
          (d?.size?.includes(bookDetails?.testament) || d?.size === "C") &&
          d?.type === "video_stream"
      ) ?? []),
    ];

    setIsVideoAvailable(filesets?.length > 0);

    if (filesets?.length > 0) {
      dispatch(
        getChapterDetailsAction({
          apiPayloadRequest: {
            filesetid: filesets?.[0]?.id ?? "",
            bookid: bookId,
            chapterid: chapterNo,
          },
          callback: (res) => {
            setBookChapterVideoDetails(res?.data?.[0] ?? []);
            setIsVideoAvailable(res?.data?.[0]?.length > 0);
            setBookChapterVideoDetailsLoading(false);
          },
        })
      );
    } else {
      setBookChapterVideoDetailsLoading(false);
      setBookChapterVideoDetails([]);
      setIsVideoAvailable(false);
    }
  };

  const fetchChapterAudioDetails = () => {
    setBookChapterAudioDetailsLoading(true);
    const bookDetails = versionDetails?.books?.find(
      (book) => book?.book_id === bookId
    );
    const filesets = [
      ...(versionDetails?.filesets?.["dbp-prod"]?.filter(
        (d) =>
          ["audio", "audio_drama"].includes(d?.type) &&
          d?.codec === "mp3" &&
          d?.size?.includes(bookDetails?.testament)
      ) ?? []),
      ...(versionDetails?.filesets?.["dbp-vid"]?.filter(
        (d) =>
          ["audio", "audio_drama"].includes(d?.type) &&
          d?.codec === "mp3" &&
          d?.size?.includes(bookDetails?.testament)
      ) ?? []),
    ];

    filesets?.sort((a, b) =>
      a?.type === "audio" ? -1 : b?.type === "audio" ? 1 : 0
    );

    setIsAudioAvailable(filesets?.length > 0);

    if (filesets?.length > 0) {
      dispatch(
        getChapterDetailsAction({
          apiPayloadRequest: {
            filesetid: filesets?.[0]?.id ?? "",
            bookid: bookId,
            chapterid: chapterNo,
          },
          callback: (res) => {
            setBookChapterAudioDetails(res?.data?.[0] ?? []);
            setIsAudioAvailable(res?.data?.[0]?.length > 0);
            setBookChapterAudioDetailsLoading(false);
          },
        })
      );
    } else {
      setBookChapterAudioDetailsLoading(false);
      setBookChapterAudioDetails([]);
      setIsAudioAvailable(false);
    }
  };

  const fetchChapterVerseDetails = () => {
    setBookChapterVerseDetailsLoading(true);
    const bookDetails = versionDetails?.books?.find(
      (book) => book?.book_id === bookId
    );
    const filesets = [
      ...(versionDetails?.filesets?.["dbp-prod"]?.filter(
        (d) =>
          (d?.size?.includes(bookDetails?.testament) || d?.size === "C") &&
          (d?.type === "text_plain" || d?.type === "text_format")
      ) ?? []),
      ...(versionDetails?.filesets?.["dbp-vid"]?.filter(
        (d) =>
          (d?.size?.includes(bookDetails?.testament) || d?.size === "C") &&
          (d?.type === "text_plain" || d?.type === "text_format")
      ) ?? []),
    ];

    filesets?.sort((a, b) =>
      a?.type === "text_plain" ? -1 : b?.type === "text_plain" ? 1 : 0
    );

    setIsTextAvailable(filesets?.length > 0);

    if (filesets?.length > 0) {
      dispatch(
        getChapterDetailsAction({
          apiPayloadRequest: {
            filesetid: filesets?.[0]?.id ?? "",
            bookid: bookId,
            chapterid: chapterNo,
          },
          callback: (res) => {
            setBookChapterVerseDetails(res?.data?.[0] ?? []);
            setIsTextAvailable(res?.data?.[0]?.length > 0);
            setBookChapterVerseDetailsLoading(false);

            if (res?.data?.[0]?.length > 0 && location?.state?.verse_start) {
              setTimeout(() => {
                const element = document.querySelector(
                  `#${location?.state?.versionid}${location?.state?.bookid}${location?.state?.chapterid}${location?.state?.verse_start}`
                );

                if (element) element.scrollIntoView({ behavior: "smooth" });
              }, 500);
            }
          },
        })
      );
    } else {
      setBookChapterVerseDetailsLoading(false);
      setBookChapterVerseDetails([]);
      setIsTextAvailable(false);
    }
  };

  useEffect(() => {
    if (versionDetails?.abbr) {
      fetchChapterVerseDetails();
      fetchChapterAudioDetails();
      fetchChapterVideoDetails();
    }
  }, [versionDetails?.abbr, bookId, chapterNo]);

  const fetchBibleSummary = (version, language) => {
    dispatch(
      getBibleSummaryAction({
        apiPayloadRequest: { versionid: version, languageid: language },
        callback: (res) => {
          setBibleSummary(res?.data?.[0]);
        },
      })
    );
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchBibleSummary(versionDetails?.abbr, bibleLanguage);
    }
  }, [versionDetails?.abbr, bibleLanguage, isAuthenticated]);

  const updatedVerseList = useMemo(() => {
    const updatedList = bookChapterVerseDetails?.map((item) => {
      const noteSummary = structuredClone(bibleSummary?.notessummary);
      (noteSummary || [])?.sort(
        (a, b) => new Date(b?.notes?.lastUpdatedDate) - new Date(a?.notes?.lastUpdatedDate)
      );
      const noteFound = noteSummary?.find(
        (d) =>
          d?.notes?.bookid === item?.book_id &&
          d?.notes?.chapterid === item?.chapter_alt &&
          d?.notes?.verse_start === item?.verse_start_alt
      );
      const verseFound = bibleSummary?.favsummary?.find(
        (d) =>
          d?.bookid === item?.book_id &&
          d?.chapterid === item?.chapter_alt &&
          d?.verseDetails?.[0]?.verse_start === item?.verse_start_alt
      );
      return {
        ...item,
        noteObjId: noteFound?.notes?.noteObjId,
        journalId: noteFound?._id,
        verseObjId: verseFound?.verseDetails?.[0]?.verseObjId,
        chapterObjId: verseFound?.chapterObjId,
      };
    });
    return updatedList;
  }, [bibleSummary, bookChapterVerseDetails]);

  const bookName =
    versionDetails?.books?.find((d) => d?.book_id === bookId)?.name ?? "";


  const fetchVerseColors = () => {
    dispatch(
      getVerseColorAction({
        apiPayloadRequest: {
          bookid: bookId,
          chapterid: chapterNo,
          versionid: versionDetails?.abbr,
        },
        callback: (response) => {
          const uppdatedSelectedColors = {};
          response?.data?.forEach((d) => {
            uppdatedSelectedColors[
              `${bookId}${d?.verseDetails?.[0]?.verse_start}`
            ] = {
              ...(d ?? {}),
              color: `#${d?.color}`,
            };
          });
          setSelectedVerses(uppdatedSelectedColors);
        },
      })
    );
  };

  useEffect(() => {
    if (isAuthenticated && bookId && chapterNo) fetchVerseColors();
  }, [isAuthenticated, bookId, chapterNo]);

  const fetchVerseDetails = (
    verse_start,
    verse_end,
    verse_text,
    responseCallback = null
  ) => {
    dispatch(
      getVerseDetailsAction({
        apiPayloadRequest: {
          languageid: bibleLanguage,
          versionid: versionDetails?.abbr,
          bookid: bookId,
          bookname: bookName,
          chapterid: chapterNo,
          verse_start: verse_start,
          verse_end: verse_end,
          verseText: verse_text,
        },
        callback: (response) => {
          if (responseCallback !== null) {
            responseCallback(response?.data?.[0]);
          }
        },
      })
    );
  };

  useEffect(() => {
    if (
      isAuthenticated &&
      !bibleParallelMode &&
      bookChapterVerseDetails?.length > 0 &&
      versionDetails?.abbr
    ) {
      fetchVerseDetails(
        bookChapterVerseDetails?.[0]?.verse_start,
        bookChapterVerseDetails?.[0]?.verse_end,
        bookChapterVerseDetails?.[0]?.verse_text,
        (data) => updateActiveVerseDetails(data)
      );
    }
  }, [
    bibleParallelMode,
    bookChapterVerseDetails,
    versionDetails,
    isAuthenticated,
  ]);


  return (
    <>
      {bookChapterVerseDetailsLoading ? (
        <div className="flex w-full justify-center">
          <div className="flex w-full flex-col gap-3 p-6 md:w-5/12">
            <div className="mb-4 h-6 w-7/12 animate-pulse rounded-2xl bg-zinc-600"></div>
            <div className="h-6 w-10/12 animate-pulse rounded-2xl bg-zinc-600"></div>
            <div className="h-6 w-full animate-pulse rounded-2xl bg-zinc-600"></div>
            <div className="h-6 w-7/12 animate-pulse rounded-2xl bg-zinc-600"></div>
            <div className="h-6 w-10/12 animate-pulse rounded-2xl bg-zinc-600"></div>
            <div className="h-6 w-full animate-pulse rounded-2xl bg-zinc-600"></div>
            <div className="h-6 w-7/12 animate-pulse rounded-2xl bg-zinc-600"></div>
            <div className="h-6 w-10/12 animate-pulse rounded-2xl bg-zinc-600"></div>
            <div className="h-6 w-full animate-pulse rounded-2xl bg-zinc-600"></div>
            <div className="h-6 w-7/12 animate-pulse rounded-2xl bg-zinc-600"></div>
            <div className="h-6 w-10/12 animate-pulse rounded-2xl bg-zinc-600"></div>
            <div className="h-6 w-full animate-pulse rounded-2xl bg-zinc-600"></div>
            <div className="h-6 w-7/12 animate-pulse rounded-2xl bg-zinc-600"></div>
          </div>
        </div>
      ) : (
        <div
          className={`flex w-full flex-col gap-5 px-2 md:w-2/4 md:px-20 ${className} ${
            globalBibleParallelMode && isMobile
              ? "h-[30vh] overflow-y-auto"
              : ""
          }`}
        >
          <div
            className={`flex items-center gap-10 ${isTextAvailable ? "flex-row" : "flex-col"}`}
          >
            <p className="text-2xl font-black text-yellowish">
              {bookName}&nbsp;{chapterNo}
            </p>

            <AudioVideoCompModel
              isTextAvailable={isTextAvailable}
              isAudioAvailable={isAudioAvailable}
              isVideoAvailable={isVideoAvailable}
              versionDetails={versionDetails}
              bookChapterAudioDetails={bookChapterAudioDetails}
              bookChapterAudioDetailsLoading={bookChapterAudioDetailsLoading}
              bookChapterVideoDetails={bookChapterVideoDetails}
              bookChapterVideoDetailsLoading={bookChapterVideoDetailsLoading}
              bibleParallelMode={bibleParallelMode}
              verseDetails={versionDetails}
            />
          </div>
          {globalBibleParallelMode &&
            bookName &&
            versionDetails?.abbr &&
            versionDetails?.name && (
              <div className="flex items-center gap-2 text-xs">
                {!bibleParallelMode && (
                  <BibleVersionControl containerKey="bibleChapterVersion" />
                )}
                {bibleParallelMode && (
                  <ParallelVerses containerKey="parallelChapterBibleVersion" />
                )}
                <p className="text-[0.8rem] text-white">
                  {versionDetails?.name}
                </p>
              </div>
            )}
          <div className="">
            <h3 className="pb-3 text-lg font-semibold text-white">{""}</h3>

            <ul className="text-greyish">
              {updatedVerseList?.map((item, index) => {
                return (
                  <li
                    key={`version-item${item?.verse_start}`}
                    className={`my-2 flex w-fit cursor-pointer items-start rounded-md ${
                      active === index ? "border-b" : ""
                    } ${
                      selectedVerseItems?.[getVerseSelectionKey(item)]
                        ? "outline outline-1 outline-yellowish/80"
                        : ""
                    } ${
                      selectedVerses?.[`${bookId}${item?.verse_start}`]?.color
                        ? "text-black"
                        : ""
                    }`}
                    onClick={() => {
                      if (selectedVerseCount > 0) {
                        toggleVerseMultiSelection(index, item);
                      } else {
                        setActive(index);
                        setActiveVerseDetails(item);
                      }
                    }}
                  >
                    <span
                      className="relative whitespace-nowrap p-0.5 leading-[19.5px]"
                      style={{
                        background:
                          selectedVerses?.[`${bookId}${item?.verse_start}`]
                            ?.color ?? "",
                      }}
                    >
                      <span className="absolute -left-1 top-0 flex -translate-x-full items-center gap-0.5">
                        {item?.verseObjId && (
                          <MdOutlineBookmark
                            className="cursor-pointer text-2xl text-yellowish"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveVerse(
                                item?.chapterObjId,
                                item?.verseObjId
                              );
                            }}
                          />
                        )}
                        {item?.journalId && (
                          <img
                            src={HoverJournals}
                            alt="HoverJournals"
                            className="min-w-5 max-w-5"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveVerseDetails(null);
                              setExistingJournalId(item?.journalId);
                              setIsNotesActive(true);
                            }}
                          />
                        )}
                      </span>
                      {item?.verse_start}
                    </span>
                    <VerseDetails
                      item={item}
                      index={index}
                      active={active}
                      bookId={bookId}
                      versionId={versionDetails?.abbr}
                      bookName={bookName}
                      chapterNo={chapterNo}
                      setActive={setActive}
                      verseText={item?.verse_text}
                      isNotesActive={isNotesActive}
                      parallelMode={bibleParallelMode}
                      handleSaveVerse={handleSaveVerse}
                      handleSelectVerse={() => toggleVerseMultiSelection(index, item)}
                      setIsNotesActive={setIsNotesActive}
                      handleColorChange={handleColorChange}
                      handleRemoveColor={handleRemoveColor}
                      setIsNotesPopupActive={setIsNotesPopupActive}
                      selectedVerses={selectedVerses}
                      isSelectedForBatch={!!selectedVerseItems?.[getVerseSelectionKey(item)]}
                      bgColor={
                        selectedVerses?.[`${bookId}${item?.verse_start}`]
                          ?.color ?? ""
                      }
                    />
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}

      {selectedVerseCount > 0 && (
        <div className="fixed bottom-5 left-1/2 z-50 flex max-w-[92vw] -translate-x-1/2 flex-wrap items-center justify-center gap-3 rounded-2xl border border-yellowish bg-greyishV2/95 px-4 py-3 text-white shadow-2xl backdrop-blur">
          <span className="text-sm font-semibold text-yellowish">
            {selectedVerseCount} {selectedVerseCount === 1 ? "verse" : "verses"} selected
          </span>
          {isAuthenticated && (
            <>
              <button
                className="rounded-full border border-greyish px-3 py-1 text-sm text-greyish"
                disabled={isMultiActionBusy}
                onClick={handleMultiRemoveColor}
              >
                Clear Highlight
              </button>
              <div className="relative">
                <button
                  className="rounded-full border border-yellowish px-3 py-1 text-sm text-yellowish"
                  disabled={isMultiActionBusy}
                  onClick={() => setIsMultiColorPaletteOpen((prev) => !prev)}
                >
                  Highlight
                </button>
                {isMultiColorPaletteOpen && (
                  <div className="absolute bottom-10 left-1/2 grid w-40 -translate-x-1/2 grid-cols-5 gap-2 rounded-xl bg-blackish p-3 shadow-xl">
                    {versesColor?.map((color, index) => (
                      <button
                        key={`${color}${index}`}
                        className="aspect-square rounded"
                        disabled={isMultiActionBusy}
                        style={{ background: color }}
                        onClick={() => handleMultiColorChange(color)}
                      />
                    ))}
                  </div>
                )}
              </div>
              <button
                className="rounded-full border border-yellowish px-3 py-1 text-sm text-yellowish"
                disabled={isMultiActionBusy}
                onClick={handleMultiSaveVerse}
              >
                Save
              </button>
            </>
          )}
          <button
            className="rounded-full border border-yellowish px-3 py-1 text-sm text-yellowish"
            disabled={isMultiActionBusy}
            onClick={handleMultiCopy}
          >
            Copy
          </button>
          <button
            className="rounded-full border border-greyish px-3 py-1 text-sm text-greyish"
            disabled={isMultiActionBusy}
            onClick={clearMultiSelection}
          >
            Clear
          </button>
        </div>
      )}

      {isNotesPopupActive && (
        <VersesModel
          active={active}
          setActive={setActive}
          isNotesActive={isNotesActive}
          setIsNotesActive={setIsNotesActive}
          isNotesPopupActive={isNotesPopupActive}
          setIsNewNotesActive={setIsNewNotesActive}
          setIsNotesPopupActive={setIsNotesPopupActive}
          setExistingJournalId={setExistingJournalId}
          gridItems={isMobile ? 2 : 4}
        />
      )}
      {isNotesActive && (
        <NotesModel
          bookName={bookName}
          isNotesActive={isNotesActive}
          isNewNotesActive={isNewNotesActive}
          setIsNotesActive={() => {
            setIsNotesActive(false);
            setExistingJournalId(null);
            setActiveVerseDetails(null);
          }}
          activeVerseDetails={activeVerseDetails}
          setActiveVerseDetails={setActiveVerseDetails}
          existingJournalId={existingJournalId}
          versionDetails={versionDetails}
          onNoteSave={() => {
            fetchBibleSummary(versionDetails?.abbr, bibleLanguage);
          }}
        />
      )}
    </>
  );
};

export default ChapterDetails;
